/**
 * The cinema: one screen, a film every showing. The programme runs on the real clock (the same for every player:
 * a showing is SHOWING seconds, the films take turns), so everyone in the room watches the same frame. A ticket
 * gets you into this showing or the next; popcorn and a drink from the counter. Sitting through a film is a good
 * evening: fun, a little rest, and (the first time) it goes on your list of films seen.
 */
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { localPrice } from './character.ts';

const NAMES: Record<string, string> = {
  lagos: 'Starlight Cinemas, Lekki', us: 'The Paramount Lights, Manhattan', france: 'Cinéma Le Lumière, Paris',
  china: 'Golden Reel Cinema, Shanghai', japan: 'Ginza Moonlight Theatre, Tokyo', italy: 'Cinema Aurora, Rome', uae: 'Dune Screens, Dubai',
};
export const cinemaFor = (region: string): string => NAMES[region] ?? NAMES.lagos;

/** the films (invented; the game paints them), each with its genre and a line for the box office */
export const FILMS = [
  { id: 'lagosheat', title: 'Lagos Heat', genre: 'Action', blurb: 'One night, one bridge, one car that won\'t stop.' },
  { id: 'calabar', title: 'Two Tickets to Calabar', genre: 'Romance', blurb: 'She missed the bus. He missed his flight. They didn\'t miss each other.' },
  { id: 'orbit', title: 'Orbit', genre: 'Sci-fi', blurb: 'The last ship home has room for one.' },
  { id: 'allen', title: 'The House on Allen Avenue', genre: 'Horror', blurb: 'The lights go off at midnight. Something switches them back on.' },
  { id: 'tortoise', title: 'Tortoise and the Drum', genre: 'Animation', blurb: 'The cleverest tortoise in the forest wants the king\'s drum.' },
] as const;
export type FilmId = (typeof FILMS)[number]['id'];

/** a showing, in real seconds: the film, then the lights up and the ads before the next */
export const SHOWING = 150, FILM_LEN = 120;
/** which showing it is now (the same for everyone), what's on, how far in */
export function showingNow(nowMs = Date.now()): { n: number; film: (typeof FILMS)[number]; t: number; next: (typeof FILMS)[number]; inFilm: boolean; untilNext: number } {
  const s = nowMs / 1000, n = Math.floor(s / SHOWING), t = s - n * SHOWING;
  return { n, film: FILMS[n % FILMS.length], t, next: FILMS[(n + 1) % FILMS.length], inFilm: t < FILM_LEN, untilNext: SHOWING - t };
}

export const cinemaTicket = (s: LifeState) => localPrice(s, 5_000, 500);
export const popcornPrice = (s: LifeState) => localPrice(s, 3_500, 500);

interface CinemaState { ticket?: number; seen?: string[]; snack?: number }
const of = (s: LifeState): CinemaState => ((s as LifeState & { cinema?: CinemaState }).cinema ??= {});
/** your ticket is for this showing or the next (by number) */
export const hasCinemaTicket = (s: LifeState, n = showingNow().n) => of(s).ticket !== undefined && (of(s).ticket === n || of(s).ticket === n + 1);
export const filmsSeen = (s: LifeState) => of(s).seen ?? [];

export function buyCinemaTicket(s: LifeState, forNext: boolean): { ok: boolean; line: string } {
  const now = showingNow();
  if (!spend(s, cinemaTicket(s), `Cinema ticket: ${(forNext ? now.next : now.film).title}`)) return { ok: false, line: "You can't afford a ticket." };
  of(s).ticket = forNext ? now.n + 1 : now.n;
  return { ok: true, line: forNext ? `A ticket for ${now.next.title}. It starts in ${Math.ceil(now.untilNext / 60)} minute${now.untilNext > 60 ? 's' : ''}.` : `A ticket for ${now.film.title}. Screen 1, straight through the doors.` };
}

export function buySnack(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!spend(s, popcornPrice(s), 'Popcorn and a drink')) return { ok: false, line: "You can't afford it.", events: [] };
  of(s).snack = s.minutes;
  s.needs.hunger = Math.min(100, s.needs.hunger + 14);
  s.needs.fun = Math.min(100, s.needs.fun + 2);
  return { ok: true, line: 'A bucket of popcorn, warm and too salty, and a drink the size of your head.', events: advance(s, 4, { busy: true }) };
}

/** You sat through it (`share`: how much of it you saw, 0..1). */
export function watchedFilm(s: LifeState, film: FilmId, share: number): { line: string; events: LifeEvent[] } {
  const c = of(s), f = FILMS.find((x) => x.id === film)!, first = !(c.seen ??= []).includes(film), full = share > 0.6;
  c.ticket = undefined;
  if (full && first) { c.seen.push(film); if (c.seen.length === 1) s.history.push({ t: s.minutes, text: `Went to the cinema: ${f.title}.` }); }
  s.needs.fun = Math.min(100, s.needs.fun + (full ? 20 : 6));
  s.needs.energy = Math.min(100, s.needs.energy + 4);
  if (c.snack !== undefined && s.minutes - c.snack < 180) s.needs.fun = Math.min(100, s.needs.fun + 4);
  const line = full ? ({ Action: 'The lights come up. Your heart is still on that bridge.', Romance: 'The lights come up. Somebody two rows back is sniffing.', 'Sci-fi': 'The lights come up and the room feels very small after all that space.', Horror: 'The lights come up. You laugh, a bit too loud.', Animation: 'The lights come up. You hum the drum song all the way out.' } as Record<string, string>)[f.genre] : 'You leave before the end.';
  return { line, events: advance(s, Math.round(120 * Math.max(0.25, share)), { busy: true }) };
}

/**
 * Screen 2, the classics: full-length films in the public domain (the Internet Archive's copies), on a loop by the
 * real clock like Screen 1, so everyone in the room is at the same moment of the same film. A day's ticket; stay as
 * long as you like; what you watched is the evening you had.
 */
export const CLASSICS = [
  { id: 'his-girl-friday', title: 'His Girl Friday', year: 1940, genre: 'Comedy', blurb: 'A newspaper editor, his ex-wife, his best reporter: the same woman, and she\'s about to remarry. The fastest talking in the movies.', len: 5504 },
  { id: 'the-general', title: 'The General', year: 1926, genre: 'Silent comedy', blurb: 'Buster Keaton, a train engineer, and the locomotive he loves more than anything. A silent film: the music is the band in the pit.', len: 4039 },
] as const;
export type ClassicId = (typeof CLASSICS)[number]['id'];
const BREAK = 300;
const LOOP = CLASSICS.reduce((a, f) => a + f.len + BREAK, 0);
/** what's on in Screen 2 now: the film, how far in (seconds), or the break before the next */
export function classicNow(nowMs = Date.now()): { film: (typeof CLASSICS)[number]; t: number; inFilm: boolean; untilNext: number; next: (typeof CLASSICS)[number] } {
  let t = (nowMs / 1000) % LOOP;
  for (let i = 0; i < CLASSICS.length; i++) {
    const f = CLASSICS[i], next = CLASSICS[(i + 1) % CLASSICS.length];
    if (t < f.len) return { film: f, t, inFilm: true, untilNext: f.len - t + BREAK, next };
    if (t < f.len + BREAK) return { film: f, t, inFilm: false, untilNext: f.len + BREAK - t, next };
    t -= f.len + BREAK;
  }
  return { film: CLASSICS[0], t: 0, inFilm: true, untilNext: CLASSICS[0].len, next: CLASSICS[1] };
}
interface ClassicState { day?: number; watched?: Record<string, number> }
const cof = (s: LifeState): ClassicState => ((s as LifeState & { classics?: ClassicState }).classics ??= {});
export const hasClassicTicket = (s: LifeState) => cof(s).day === Math.floor(s.minutes / 1440);
export const classicTicket = (s: LifeState) => localPrice(s, 4_000, 500);
export function buyClassicTicket(s: LifeState): { ok: boolean; line: string } {
  if (hasClassicTicket(s)) return { ok: true, line: 'Your Screen 2 ticket is good all day.' };
  if (!spend(s, classicTicket(s), 'Cinema ticket: Screen 2, the classics')) return { ok: false, line: "You can't afford a ticket." };
  cof(s).day = Math.floor(s.minutes / 1440);
  return { ok: true, line: `A ticket for Screen 2: ${classicNow().film.title} (${classicNow().film.year}), all day. Come and go as you like.` };
}
/** You watched `realSeconds` of a classic (an old film is better the longer you give it). */
export function watchedClassic(s: LifeState, film: ClassicId, realSeconds: number): { line: string; events: LifeEvent[] } {
  const c = cof(s), f = CLASSICS.find((x) => x.id === film)!, mins = realSeconds / 60;
  const total = ((c.watched ??= {})[film] = (c.watched[film] ?? 0) + mins);
  s.needs.fun = Math.min(100, s.needs.fun + Math.min(24, 3 + mins * 1.5));
  s.needs.energy = Math.min(100, s.needs.energy + Math.min(6, mins * 0.5));
  if (total >= f.len / 60 * 0.8 && total - mins < f.len / 60 * 0.8) s.history.push({ t: s.minutes, text: `Watched ${f.title} (${f.year}) at the cinema, all the way through.` });
  const line = mins < 2 ? 'You slip out after a minute or two.' : mins < 15 ? `A good ${Math.round(mins)} minutes of ${f.title}.` : f.id === 'the-general' ? 'Keaton never smiles once, and the whole room is laughing.' : 'Nobody in that film ever stops talking, and you never want them to.';
  return { line, events: advance(s, 0, { busy: true }) };
}
